import { useRef } from "react";
import MuxPlayer from "@mux/mux-player-react";
import { MinimalPlayerFrame } from "@/components/MinimalPlayerFrame";

const mono = { fontFamily: '"JetBrains Mono", monospace' } as const;
const serif = { fontFamily: '"Cormorant Garamond", serif' } as const;

export type PublicIntroVideoProps = {
  playbackId: string;
  eyebrow: string;
  title: string;
  duration: string;
  anchorId?: string;
};

/**
 * Reusable public-policy Mux video block.
 *
 * Mirrors the visual chrome of FreePreview (60px burgundy rule, mono eyebrow,
 * serif H2, italic muted duration, 1px hairline, MinimalPlayerFrame) but
 * deliberately strips: quiz state, localStorage completion, Stripe checkout,
 * post-quiz state, and the native `ended` listener that swaps to a quiz.
 *
 * Use for free/public videos that need no auth, no gating, no follow-up CTA.
 * Playback ID MUST be a Mux public-policy asset (no JWT minted).
 */
export function PublicIntroVideo({
  playbackId,
  eyebrow,
  title,
  duration,
  anchorId,
}: PublicIntroVideoProps) {
  const playerRef = useRef<any>(null);

  return (
    <section
      id={anchorId}
      className="max-w-[720px] pt-[64px] md:pt-[96px]"
    >
      <hr className="w-[60px] border-0 h-[2px] bg-burgundy" />
      <p
        className="mt-6 text-[11px] font-bold uppercase tracking-[0.05em] text-burgundy"
        style={mono}
      >
        {eyebrow}
      </p>
      <h2 className="mt-6 text-[24px] md:text-[32px] font-bold leading-[1.2] text-ink">
        {title}
      </h2>
      <p
        className="mt-4 text-[18px] italic leading-[1.6]"
        style={{ ...serif, color: "#5A534A" }}
      >
        {duration}. No signup. No email. Just watch.
      </p>

      <hr className="mt-4 mb-6 w-full border-0 h-px bg-burgundy" />

      <MinimalPlayerFrame playerRef={playerRef as any} maxWidth="720px">
        <MuxPlayer
          ref={playerRef as any}
          playbackId={playbackId}
          streamType="on-demand"
          preload="metadata"
          poster={`https://image.mux.com/${playbackId}/thumbnail.jpg?time=1`}
          metadata={{ video_title: `WHO KNEW · ${title}` }}
          accentColor="#8B1E1E"
          defaultHiddenCaptions
          nohotkeys
          playsInline
          loop={false}
          style={{ width: "100%", height: "100%", display: "block" }}
        />
      </MinimalPlayerFrame>
    </section>
  );
}
